import React, { useState, useEffect } from 'react';
import { Save, Mail, Database, Lock, Globe, User, UserPlus, Trash2 } from 'lucide-react';
import { useAuth } from '../../App';
import { getSettings, updateSettings, changePassword, changeEmail, getAdmins, createAdmin, deleteAdmin } from '../../api/settings';
import AdminLayout from './AdminLayout';

const AdminSettings = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '' });
  const [emailData, setEmailData] = useState({ new_email: '', password: '' });
  const [currentAdminEmail, setCurrentAdminEmail] = useState('');
  const [message, setMessage] = useState('');
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [data, adminsData] = await Promise.all([getSettings(token), getAdmins(token)]);
        setSettings(data);
        setCurrentAdminEmail(data.admin_email || 'admin@mrminds.pl');
        setAdmins(adminsData);
      } catch (err) {} finally { setLoading(false); }
    };
    if (token) fetchSettings();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(token, settings);
      setMessage('Ustawienia zapisane!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) { setMessage('Błąd zapisu'); } finally { setSaving(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    try {
      await changePassword(token, passwordData);
      setMessage('Hasło zmienione!');
      setPasswordData({ current_password: '', new_password: '' });
    } catch (err) { setMessage('Błąd zmiany hasła'); }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    try {
      await changeEmail(token, emailData);
      setMessage('Email admina zmieniony!');
      setCurrentAdminEmail(emailData.new_email);
      setEmailData({ new_email: '', password: '' });
    } catch (err) { setMessage('Błąd zmiany email (sprawdź hasło)'); }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (newAdmin.password.length < 8) { setMessage('Hasło musi mieć minimum 8 znaków'); return; }
    setCreatingAdmin(true);
    try {
      await createAdmin(token, newAdmin);
      setMessage('Konto admina utworzone!');
      setNewAdmin({ email: '', password: '' });
      const updated = await getAdmins(token);
      setAdmins(updated);
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Błąd tworzenia konta');
    } finally { setCreatingAdmin(false); }
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDeleteAdmin = async (adminId, adminEmail) => {
    if (!window.confirm(`Usunąć konto ${adminEmail}?`)) return;
    try {
      await deleteAdmin(token, adminId);
      setAdmins(prev => prev.filter(a => a.id !== adminId));
      setMessage('Konto usunięte');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Błąd usuwania konta');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  if (loading) return <AdminLayout title="Ustawienia"><div className="flex justify-center py-12"><div className="spinner"></div></div></AdminLayout>;

  return (
    <AdminLayout title="Ustawienia">
      {message && (
        <div className={`mb-6 p-4 rounded-xl ${message.includes('Błąd') ? 'bg-red-500/20 text-red-400' : 'bg-[#10b981]/20 text-[#10b981]'}`}>
          {message}
        </div>
      )}

      <div className="space-y-6">
        {/* Site Settings */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#10b981]/20 flex items-center justify-center text-[#10b981]">
              <Globe size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Ustawienia strony</h2>
              <p className="text-[#6B7280] text-sm">Nazwa, logo i domena</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Nazwa strony</label>
                <input type="text" value={settings.site_name || ''} onChange={e => setSettings({...settings, site_name: e.target.value})} className="w-full" />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Typ logo</label>
                <select value={settings.site_logo_type || 'text'} onChange={e => setSettings({...settings, site_logo_type: e.target.value})} className="w-full">
                  <option value="text">Tekst</option>
                  <option value="image">Obrazek</option>
                </select>
              </div>
            </div>
            {settings.site_logo_type === 'image' && (
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">URL logo</label>
                <input type="url" value={settings.site_logo || ''} onChange={e => setSettings({...settings, site_logo: e.target.value})} className="w-full" placeholder="https://..." />
              </div>
            )}
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Domena</label>
              <input type="text" value={settings.domain || ''} onChange={e => setSettings({...settings, domain: e.target.value})} className="w-full" placeholder="mrminds.pl" />
            </div>
          </div>
        </div>

        {/* Download Settings */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400">
              <Database size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Ustawienia pobierania</h2>
              <p className="text-[#6B7280] text-sm">Ważność linków i limity</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Ważność linku (dni)</label>
              <input type="number" value={settings.download_link_validity_days || 7} onChange={e => setSettings({...settings, download_link_validity_days: parseInt(e.target.value)})} className="w-full" min="1" />
            </div>
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Limit pobrań (0 = bez limitu)</label>
              <input type="number" value={settings.download_limit || 0} onChange={e => setSettings({...settings, download_limit: parseInt(e.target.value)})} className="w-full" min="0" />
            </div>
          </div>
        </div>

        {/* Email Configuration */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-400">
              <Mail size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Konfiguracja emaili</h2>
              <p className="text-[#6B7280] text-sm">Adresy nadawcy i odpowiedzi (używane we wszystkich wiadomościach)</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Email nadawcy</label>
                <input type="email" value={settings.email_from_address || ''} onChange={e => setSettings({...settings, email_from_address: e.target.value})} className="w-full" placeholder="kontakt@mrminds.pl" />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Nazwa nadawcy</label>
                <input type="text" value={settings.email_from_name || 'MrMinds'} onChange={e => setSettings({...settings, email_from_name: e.target.value})} className="w-full" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Email wsparcia (Reply-To) — klienci odpowiadają na ten adres</label>
              <input type="email" value={settings.email_support_address || ''} onChange={e => setSettings({...settings, email_support_address: e.target.value})} className="w-full" placeholder="kontakt@mrminds.pl" />
            </div>
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Email do powiadomień (admin)</label>
              <input type="email" value={settings.notification_email || ''} onChange={e => setSettings({...settings, notification_email: e.target.value})} className="w-full" placeholder="admin@mrminds.pl" />
            </div>
          </div>
        </div>

        {/* Admin Email Change */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400">
              <User size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Zmień email admina</h2>
              <p className="text-[#6B7280] text-sm">Obecny: {currentAdminEmail}</p>
            </div>
          </div>
          <form onSubmit={handleEmailChange} className="space-y-4">
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Nowy email</label>
              <input type="email" value={emailData.new_email} onChange={e => setEmailData({...emailData, new_email: e.target.value})} className="w-full" required data-testid="new-admin-email" />
            </div>
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Potwierdź hasłem</label>
              <input type="password" value={emailData.password} onChange={e => setEmailData({...emailData, password: e.target.value})} className="w-full" required data-testid="email-change-password" />
            </div>
            <button type="submit" className="btn-ghost" data-testid="change-email-btn">Zmień email</button>
          </form>
        </div>

        {/* Admin Password */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center text-red-400">
              <Lock size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Zmień hasło admina</h2>
              <p className="text-[#6B7280] text-sm">Bezpieczeństwo konta</p>
            </div>
          </div>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Obecne hasło</label>
              <input type="password" value={passwordData.current_password} onChange={e => setPasswordData({...passwordData, current_password: e.target.value})} className="w-full" required />
            </div>
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Nowe hasło</label>
              <input type="password" value={passwordData.new_password} onChange={e => setPasswordData({...passwordData, new_password: e.target.value})} className="w-full" required />
            </div>
            <button type="submit" className="btn-ghost">Zmień hasło</button>
          </form>
        </div>

        {/* Admin Accounts */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400">
              <UserPlus size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Konta administratorów</h2>
              <p className="text-[#6B7280] text-sm">Zarządzaj dostępem do panelu</p>
            </div>
          </div>

          {/* Existing admins list */}
          <div className="mb-6 space-y-2">
            {admins.map(admin => (
              <div key={admin.id} className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-lg border border-white/5">
                <div>
                  <p className="text-white text-sm font-medium">{admin.email}</p>
                  <p className="text-[#6B7280] text-xs">{admin.role} · {admin.is_active ? 'Aktywny' : 'Nieaktywny'}</p>
                </div>
                {admin.email !== currentAdminEmail && (
                  <button onClick={() => handleDeleteAdmin(admin.id, admin.email)} className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/10 transition-colors">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Create new admin */}
          <form onSubmit={handleCreateAdmin} className="space-y-4 border-t border-white/5 pt-6">
            <p className="text-sm font-medium text-white">Dodaj nowe konto</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Email</label>
                <input type="email" value={newAdmin.email} onChange={e => setNewAdmin({...newAdmin, email: e.target.value})} className="w-full" placeholder="backup@mrminds.pl" required />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Hasło (min. 8 znaków)</label>
                <input type="password" value={newAdmin.password} onChange={e => setNewAdmin({...newAdmin, password: e.target.value})} className="w-full" required />
              </div>
            </div>
            <button type="submit" disabled={creatingAdmin} className="btn-ghost">
              {creatingAdmin ? <div className="spinner"></div> : <><UserPlus size={16} /> Utwórz konto</>}
            </button>
          </form>
        </div>

        {/* Save Button */}
        <button onClick={handleSave} disabled={saving} className="btn-primary w-full py-4" data-testid="save-settings">
          {saving ? <div className="spinner"></div> : <><Save size={18} /> Zapisz wszystkie ustawienia</>}
        </button>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;